// 1. Framework / library imports
import { memo, useCallback, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. API & Types
import { deleteFleetVehicleExpenseAPI } from "services/api/fleet/vehicles/expenses/api";
import { EXPENSE_TYPE_VEHICLE_TRANSPORT } from "services/api/fleet/vehicles/expenses/consts";
import { FleetVehicleExpense } from "services/api/fleet/vehicles/expenses/types";

// 4. UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";

// 5. Local Components & Constants
import VehicleExpenseEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleExpenseEditCard/VehicleExpenseEditCard";
import { VEHICLE_TRANSFER_EXPENSE_COLUMNS } from "./consts";

// 6. Local Types
import { VehicleTransferExpensesFormProps } from "./types";

/**
 * Troškovi premještaja — prijevoz, cestarina, gorivo. Unose se postojećim modalom troška
 * vozila, pa se ne razlikuju od troškova unesenih drugdje; premještaj im se pridružuje
 * kad se spremi. Raspored prati korak vatrogasnih aparata pri unosu vozila.
 */
const VehicleTransferExpensesForm: React.FC<VehicleTransferExpensesFormProps> = (props) => {
  const { vehicleId, expenses, onChange } = props;

  const { t } = useTranslate();

  const [expenseId, setExpenseId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const handleRowClick = useCallback((id: string) => {
    setExpenseId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(() => {
    setExpenseId("");
    setShowAddNewModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setExpenseId("");
    setShowAddNewModal(false);
  }, []);

  // Modal trošak sprema sam, pa se ovdje samo osvježi popis koraka.
  const handleEditSuccess = useCallback(
    (expense?: FleetVehicleExpense) => {
      if (expense) {
        onChange(
          expenses.some((e) => e.id === expense.id)
            ? expenses.map((e) => (e.id === expense.id ? expense : e))
            : [...expenses, expense]
        );
      }

      setExpenseId("");
      setShowAddNewModal(false);
    },
    [expenses, onChange]
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setExpenseId(id);
      setOpenDialog(true);
    },
    []
  );

  // Trošak je već spremljen kad ga modal zatvori, pa ga uklanjanje briše.
  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!expenseId) return;

    try {
      await deleteFleetVehicleExpenseAPI(expenseId, t);
      onChange(expenses.filter((expense) => expense.id !== expenseId));
    } catch (err) {
      console.error("Greška prilikom brisanja troška:", err);
    }

    setOpenDialog(false);
    setExpenseId("");
  }, [expenseId, expenses, onChange, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setExpenseId("");
    setOpenDialog(false);
  }, []);

  return (
    <>
      <div className="relative flex justify-center px-4 pt-12 h-[calc(100vh-150px)]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
            Troškovi premještaja
          </h1>
          <p className="text-center text-md text-slate-600 mb-4 max-w-2xl mx-auto">
            Dodajte trošak premještaja klikom na gumb <strong>"Dodaj trošak"</strong> —
            prijevoz, cestarinu ili gorivo. Korak je neobavezan, pa premještaj možete
            spremiti i bez troškova.
          </p>
          <div className="px-3">
            {expenses.length === 0 ? (
              <div className="max-w-md mx-auto">
                <Button
                  variant="primary"
                  size="full"
                  children="Dodaj trošak"
                  onClick={handleAddNew}
                />
              </div>
            ) : (
              <>
                <TableSearch
                  columns={VEHICLE_TRANSFER_EXPENSE_COLUMNS(handleDeleteClick)}
                  data={expenses}
                  selectableRows={false}
                  disablePagination={true}
                  onRowClick={handleRowClick}
                />
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outlined"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj trošak"
                    onClick={handleAddNew}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Unos ide iz čarobnjaka premještaja, pa je manji centrirani prozor bez uvoda. */}
      <VehicleExpenseEditCard
        show={showAddNewModal}
        vehicleId={vehicleId}
        expenseId={expenseId}
        defaultExpenseTypeId={EXPENSE_TYPE_VEHICLE_TRANSPORT}
        isFullScreen={false}
        onSuccess={handleEditSuccess}
        onClose={handleModalClose}
      />

      <Dialog
        open={openDialog}
        title="Brisanje troška"
        message="Jeste li sigurni da želite obrisati trošak?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

export default memo(VehicleTransferExpensesForm);
